import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  Grid,
  GridProps,
  Text,
} from "@chakra-ui/react";
import { CustomModal } from "components/custom-modal";
import { DropdownField, DropdownFieldProps } from "components/dropdown-field";
import { InputField, InputFieldProps } from "components/input-field";
import {
  DraggableFileField,
  DraggableFileFieldProps,
} from "components/input-file";
import { CloseIcon } from "components/vectors/close-icon";

import { useState } from "react";
import { FaSave } from "react-icons/fa";
import { useEventListener, makeEvent } from "services/events";
import { fieldValidation } from "utils/field-validation";
import { ObjectSchema, objectSchema } from "utils/object-methods";

export type ModalFieldsOptions = {
  onSubmit: (param: { [key: string]: any }) => any;
  onClose?: () => any;
  onReject?: () => any;
  message?:
    | string
    | JSX.Element
    | (({
        onClose,
      }: {
        onClose: ModalFieldsOptions["onClose"];
      }) => string | JSX.Element);
  title?: JSX.Element | string;
  confirmButtonStyle?: ButtonProps;
  rejectButtonStyle?: ButtonProps;
  variant?: "normal" | "danger";
  paramsSettings: {
    containerProps?: GridProps;
    params: {
      name: string;
      field: string;
      type?: "input" | "dropdown" | "inputFile";
      initialValue?: string;
      fieldProps?: {
        InputField?: Omit<Partial<InputFieldProps>, "value" | "onChange">;
        Box?: BoxProps;
        DropdownField?: Partial<DropdownFieldProps>;
        DraggableFileField?: Partial<DraggableFileFieldProps>;
      };
    }[];
  };
  simpleValidation?: boolean;
  schema?: ObjectSchema;
};

export function ModalFields() {
  const [isLoading, setIsLoading] = useState(false);
  const [params, setParams] = useState<{ [key: string]: any }>({});
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [modalConfirmOptions, setModalConfirmOptions] =
    useState<ModalFieldsOptions | null>(null);

  function onOpen(data: ModalFieldsOptions) {
    setModalConfirmOptions(data);
    setParams(
      data.paramsSettings.params.reduce((acc, curr) => {
        acc[curr.field] = curr.initialValue ?? "";
        return acc;
      }, {} as { [key: string]: string })
    );
  }

  function onClose() {
    modalConfirmOptions?.onClose?.();
    setModalConfirmOptions(null);
    setErrors({});
    setParams({});
    setIsLoading(false);
  }

  const { onClick: rejectButtonOnClick, ...rejectButtonStyle } =
    modalConfirmOptions?.rejectButtonStyle || {};
  const { onClick: confirmButtonOnClick, ...confirmButtonStyle } =
    modalConfirmOptions?.confirmButtonStyle || {};

  const simpleValidation = (body: any) => {
    const schema = {
      ...modalConfirmOptions!.paramsSettings.params.reduce((acc, curr) => {
        acc[curr.field] = (value: any) => {
          const { isValid, errorMessage } = fieldValidation({ value }).required(
            { message: "Preecha o campo" }
          );
          return { valid: isValid, message: errorMessage };
        };
        return acc;
      }, {} as ObjectSchema),
    };
    return objectSchema(schema, body);
  };

  const onChangeField = (field: string, value: any) => {
    setParams((prev) => ({ ...prev, [field]: value }));
    setErrors((e) => {
      delete e[field];
      return { ...e };
    });
  };

  const modalFooter = modalConfirmOptions ? (
    <>
      <Button
        variant={modalConfirmOptions.variant}
        onClick={async (e) => {
          let validation: { isValid: boolean; body: any; errors: any } | null =
            null;

          if (modalConfirmOptions.schema) {
            validation = objectSchema(modalConfirmOptions.schema, params);
          } else if (modalConfirmOptions.simpleValidation) {
            validation = simpleValidation(params);
          }

          if (validation && !validation.isValid) {
            return setErrors(validation.errors);
          }
          setIsLoading(true);
          try {
            await modalConfirmOptions.onSubmit(params);
          } catch {
          } finally {
            setIsLoading(false);
            onClose();
            confirmButtonOnClick?.(e);
          }
        }}
        isLoading={isLoading}
        children="Salvar"
        {...confirmButtonStyle}
      />{" "}
      <Button
        variant="outline"
        onClick={(e) => {
          modalConfirmOptions.onReject?.();
          onClose();
          rejectButtonOnClick?.(e);
        }}
        leftIcon={<CloseIcon />}
        children="Cancelar"
        isDisabled={isLoading}
        {...rejectButtonStyle}
      />
    </>
  ) : undefined;

  useEventListener("openModalFields", (data: ModalFieldsOptions) => {
    if (modalConfirmOptions !== null) {
      new Error("The confirmation modal is already open");
    } else onOpen(data);
  });

  return (
    <>
      <CustomModal
        isOpen={modalConfirmOptions !== null}
        onClose={onClose}
        modalFooter={modalFooter}
        modalTitle={modalConfirmOptions?.title ?? ""}
        size="md"
      >
        <Flex alignItems="center" gap="10px">
          {typeof modalConfirmOptions?.message === "function"
            ? modalConfirmOptions.message({ onClose })
            : modalConfirmOptions?.message
            ? modalConfirmOptions.message
            : null}

          {modalConfirmOptions?.paramsSettings ? (
            <Grid
              w="100%"
              gap="12px"
              {...modalConfirmOptions?.paramsSettings.containerProps}
            >
              {modalConfirmOptions.paramsSettings.params.map(
                ({ fieldProps, name, field, type = "input" }, i) => {
                  if (type === "input") {
                    return (
                      <Box key={i} {...fieldProps?.Box}>
                        <InputField
                          title={name}
                          {...fieldProps?.InputField}
                          value={params[field]}
                          onChange={(e) => onChangeField(field, e.target.value)}
                          errorMessage={errors[field]}
                        />
                      </Box>
                    );
                  } else if (type === "dropdown") {
                    return (
                      <Box key={i} {...fieldProps?.Box}>
                        <DropdownField
                          title={name}
                          {...fieldProps?.DropdownField}
                          value={params[field]}
                          onChange={(value) => onChangeField(field, value)}
                          errorMessage={errors[field]}
                        />
                      </Box>
                    );
                  } else if (type === "inputFile") {
                    return (
                      <Box key={i} {...fieldProps?.Box}>
                        <Text mb="8px" fontWeight="medium">
                          {name}
                        </Text>
                        <DraggableFileField
                          fileExtensions={[]}
                          {...fieldProps?.DraggableFileField}
                          file={params[field]}
                          handleDelete={() => onChangeField(field, undefined)}
                          handleFile={(file) => onChangeField(field, file)}
                          error={errors[field] || ""}
                        />
                      </Box>
                    );
                  }
                }
              )}
            </Grid>
          ) : null}
        </Flex>
      </CustomModal>
    </>
  );
}

export function openModalFields(options: ModalFieldsOptions) {
  makeEvent("openModalFields", options);
}
